/**
 * Shared types for assignment configuration components (Phase 1b)
 *
 * A configuração de distribuição agora vem direta do `@troia-v3/types`
 * (`DistributionConfig`) — não existe mais conversão entre
 * `FunnelAssignmentConfig`/`ChannelAssignmentConfig` e um tipo "unificado"
 * local. Cada canal/funil/pipeline carrega um `DistributionConfig` no
 * campo `assignmentConfig`, mesmo shape em todos.
 *
 * Ver plano: DOCS/architecture/ASSIGNMENTS_REMOVAL.md
 */

import type { DistributionConfig, DistributionStrategy } from '@troia-v3/types';

// Re-export para conveniência dos consumidores locais.
export type { DistributionConfig, DistributionStrategy } from '@troia-v3/types';

// ============================================================================
// COMPONENT PROPS
// ============================================================================

export interface AssignmentConfigSectionProps {
  /** Current distribution configuration */
  config: DistributionConfig;
  /** Callback when config changes */
  onChange: (config: DistributionConfig) => void;

  /** Context type - determines data fetching behavior */
  resourceType: 'channel' | 'funnel' | 'pipeline';
  /** Resource ID (channelId / funnelId / pipelineId) - optional for new resources */
  resourceId?: string;

  /** Visual variant */
  variant?: 'card' | 'inline';
}

// ============================================================================
// STRATEGY OPTIONS (UI labels)
// ============================================================================

export interface StrategyOption {
  value: DistributionStrategy;
  label: string;
  description: string;
}

export const STRATEGY_OPTIONS: StrategyOption[] = [
  {
    value: 'sequential',
    label: 'Sequencial',
    description: 'Atribui de forma sequencial entre os atendentes do contexto',
  },
  {
    value: 'fixed_operator',
    label: 'Operador Fixo',
    description: 'Sempre atribui ao mesmo operador',
  },
  {
    value: 'fixed_team',
    label: 'Equipe Fixa',
    description: 'Atribui entre membros de uma equipe específica (rotação sequencial)',
  },
  {
    value: 'shift',
    label: 'Por Turno',
    description: 'Atribui apenas a usuários do turno ativo (rotação sequencial)',
  },
  {
    value: 'availability',
    label: 'Por Disponibilidade',
    description: 'Atribui apenas a usuários online no momento (rotação sequencial)',
  },
  {
    value: 'last_interaction',
    label: 'Última Interação',
    description: 'Atribui para quem interagiu por último com o contato',
  },
];

// ============================================================================
// DATA TYPES (for API responses in selectors)
// ============================================================================

export interface UserOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  status: 'active' | 'inactive';
}

export interface TeamOption {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'inactive';
}

